/**
 * Care Teams API Client Functions
 */
import { apiClient } from '../api-client';

// ============= Type Definitions =============

export interface DoctorSummary {
  id: number;
  name: string;
  specialization?: string;
}

export interface TherapistSummary {
  id: number;
  name: string;
  specialization?: string;
}

export interface NurseSummary {
  id: number;
  name: string;
  specialization?: string;
}

export interface CareTeam {
  id: number;
  name: string;
  description?: string;
  hospital_id?: number;
  therapist_id?: number;
  nurse_id?: number;
  created_by_coordinator_id?: number;
  is_active: boolean;
  doctors: DoctorSummary[];
  therapist?: TherapistSummary;
  nurse?: NurseSummary;
  hospital_name?: string;
  created_by_name?: string;
  patient_count: number;
}

export interface CareTeamCreate {
  name: string;
  description?: string;
  hospital_id?: number;
  doctor_ids: number[]; // At least one doctor required
  therapist_id?: number;
  nurse_id?: number;
}

export interface CareTeamUpdate {
  name?: string;
  description?: string;
  doctor_ids?: number[];
  therapist_id?: number;
  nurse_id?: number;
  hospital_id?: number;
  is_active?: boolean;
}

export interface CareTeamListResponse {
  total: number;
  page: number;
  page_size: number;
  care_teams: CareTeam[];
}

// ============= API Functions =============

export const careTeamsApi = {
  /**
   * Get list of all care teams with filters
   */
  async getAll(params?: {
    hospital_id?: number;
    is_active?: boolean;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<CareTeamListResponse> {
    const response = await apiClient.get('/care-teams', params);
    return response;
  },

  /**
   * Get care team by ID
   */
  async getById(careTeamId: number): Promise<CareTeam> {
    const response = await apiClient.get(`/care-teams/${careTeamId}`);
    return response;
  },

  /**
   * Create new care team (requires COORDINATOR role)
   */
  async create(data: CareTeamCreate): Promise<{ status: string; message: string; care_team: CareTeam }> {
    const response = await apiClient.post('/care-teams', data);
    return response;
  },

  /**
   * Update care team (requires COORDINATOR role)
   */
  async update(
    careTeamId: number,
    data: CareTeamUpdate
  ): Promise<{ status: string; message: string; care_team: CareTeam }> {
    const response = await apiClient.put(`/care-teams/${careTeamId}`, data);
    return response;
  },

  /**
   * Delete care team (requires COORDINATOR role)
   */
  async delete(careTeamId: number): Promise<{ status: string; message: string }> {
    const response = await apiClient.delete(`/care-teams/${careTeamId}`);
    return response;
  },
};